import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, PageHeader, Button, Input, Label, Select, Table, Th, Td } from "../components/ui";
import { fmtIDR, fmtDate, errMsg } from "../lib/utils";
import { toast } from "sonner";
import { Receipt, ChevronDown, ChevronRight, CheckCircle2, Banknote, X, UserMinus, UserPlus, Pencil, RotateCcw, FileSpreadsheet } from "lucide-react";
import NumberInput from "../components/NumberInput";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const monthName = (ym) => `${BULAN[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
const STATUS_BADGE = {
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  unpaid: "bg-amber-50 text-amber-800 border-amber-200",
  none: "bg-slate-50 text-slate-500 border-slate-200",
};
const STATUS_LABEL = { paid: "Sudah Dibayar", unpaid: "Belum Dibayar", none: "Nihil" };
const SOURCE_LABEL = { payroll: "Payroll", estimasi: "Estimasi gaji pokok", belum_join: "Belum join", manual: "Manual" };

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function BrutoCell({ month, s, onSave, busy }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(String(s.pph21_bruto || ""));
  const editable = month.status !== "paid" && s.source !== "payroll";
  if (edit) {
    const save = () => { setEdit(false); if (Number(val) !== s.pph21_bruto) onSave(month, s.employee_id, val === "" ? null : Number(val)); };
    return (
      <div className="w-36 ml-auto">
        <NumberInput autoFocus data-testid={`pph21-bruto-input-${month.period_month}-${s.employee_id}`} value={val}
          onChange={(e) => setVal(e.target.value)} onBlur={save}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setVal(String(s.pph21_bruto || "")); setEdit(false); } }}/>
      </div>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 justify-end">
      <span className={s.source === "manual" ? "text-blue-700 font-semibold" : ""}>{fmtIDR(s.pph21_bruto)}</span>
      {editable && (
        <button disabled={busy} data-testid={`pph21-bruto-edit-${month.period_month}-${s.employee_id}`} title="Ubah bruto manual"
          onClick={() => { setVal(String(s.pph21_bruto || "")); setEdit(true); }} className="p-1 rounded hover:bg-blue-50 text-blue-700 disabled:opacity-40"><Pencil size={12}/></button>
      )}
      {editable && s.source === "manual" && (
        <button disabled={busy} data-testid={`pph21-bruto-reset-${month.period_month}-${s.employee_id}`} title="Kembalikan ke estimasi gaji pokok"
          onClick={() => onSave(month, s.employee_id, null)} className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-40"><RotateCcw size={12}/></button>
      )}
    </span>
  );
}

function EmployeeTable({ month, onToggle, onSaveBruto, busy }) {
  const locked = month.status === "paid";
  return (
    <Table>
      <thead><tr>
        <Th>Karyawan</Th><Th>PTKP</Th><Th>Kategori TER</Th><Th className="text-right">Tarif</Th><Th>Sumber</Th>
        <Th className="text-right">Bruto</Th><Th className="text-right">PPh21</Th><Th className="text-right">Aksi</Th>
      </tr></thead>
      <tbody>
        {month.rows.map((s) => (
          <tr key={s.employee_id} data-testid={`pph21-emp-${month.period_month}-${s.employee_id}`} className={s.excluded ? "opacity-50 bg-slate-100" : ""}>
            <Td className={s.excluded ? "line-through" : ""}>{s.employee_name}{s.employee_no ? <span className="text-slate-400 text-xs ml-1">({s.employee_no})</span> : null}</Td>
            <Td>{s.ptkp_status}</Td>
            <Td>{s.ter_category || "-"}</Td>
            <Td className="text-right">{s.ter_rate != null ? `${(s.ter_rate * 100).toFixed(2)}%` : "Progresif (Des)"}</Td>
            <Td className="text-xs text-slate-500">{SOURCE_LABEL[s.source] || s.source}</Td>
            <Td className="text-right font-mono whitespace-nowrap"><BrutoCell month={month} s={s} onSave={onSaveBruto} busy={busy}/></Td>
            <Td className="text-right font-mono font-bold">{s.excluded ? "—" : fmtIDR(s.pph21)}</Td>
            <Td className="text-right">
              {!locked && (
                <button
                  data-testid={`pph21-toggle-${month.period_month}-${s.employee_id}`}
                  disabled={busy}
                  onClick={() => onToggle(month, s.employee_id)}
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded disabled:opacity-40 ${s.excluded ? "text-emerald-700 hover:bg-emerald-50" : "text-red-600 hover:bg-red-50"}`}
                >
                  {s.excluded ? <><UserPlus size={13}/> Kembalikan</> : <><UserMinus size={13}/> Take out</>}
                </button>
              )}
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function PayModal({ month, cashAccounts, onClose, onPaid }) {
  const [form, setForm] = useState({ cash_account_code: cashAccounts[0]?.code || "1101", expense_date: todayStr() });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/hr/pph21/month/${month.period_month}/pay`, form);
      toast.success("PPh21 berhasil dibayar & tercatat sebagai expense");
      onPaid();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="pph21-pay-modal">
      <Card className="w-full max-w-md p-0 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 font-bold"><Receipt size={16} className="text-emerald-600"/> Bayar PPh21 — {monthName(month.period_month)}</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="p-3 rounded bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] uppercase font-bold text-emerald-700">Total PPh21 Dibayar · {month.employee_count} karyawan</div>
            <div className="font-heading font-black text-xl text-emerald-900">{fmtIDR(month.total_pph21)}</div>
          </div>
          <div>
            <Label>Rekening Sumber Pembayaran</Label>
            <Select data-testid="pph21-cash-account" value={form.cash_account_code} onChange={(e) => setForm({ ...form, cash_account_code: e.target.value })}>
              {cashAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
            </Select>
          </div>
          <div>
            <Label>Tanggal Pembayaran</Label>
            <Input type="date" data-testid="pph21-expense-date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })}/>
          </div>
          <p className="text-xs text-slate-500">Jurnal: Dr Beban PPh21 Karyawan (6106) / Cr Kas. Akan muncul di halaman Expenses.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button data-testid="pph21-confirm-pay" variant="success" disabled={saving} onClick={submit}><Banknote size={14}/> {saving ? "Memproses..." : "Bayar & Catat Expense"}</Button>
        </div>
      </Card>
    </div>
  );
}

export default function Pph21() {
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [months, setMonths] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hr/pph21/year", { params: { year } }).then((r) => setMonths(r.data.months || [])).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [year]); // eslint-disable-line
  useEffect(() => { api.get("/accounts", { params: { is_cash: true } }).then((r) => setCashAccounts(r.data || [])); }, []);

  const toggleEmp = async (month, eid) => {
    const current = month.rows.filter((r) => r.excluded).map((r) => r.employee_id);
    const next = current.includes(eid) ? current.filter((x) => x !== eid) : [...current, eid];
    setBusy(true);
    try {
      await api.put(`/hr/pph21/month/${month.period_month}/exclusions`, { excluded_employee_ids: next });
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveBruto = async (month, eid, bruto) => {
    setBusy(true);
    try {
      await api.put(`/hr/pph21/month/${month.period_month}/bruto`, { employee_id: eid, bruto });
      toast.success(bruto == null ? "Bruto dikembalikan ke estimasi" : "Bruto manual disimpan");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const yearTotal = months.reduce((s, m) => s + (m.total_pph21 || 0), 0);
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/hr/pph21/export", { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Karyawan_PPh21_${year}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal export Excel"); } finally { setExporting(false); }
  };

  return (
    <div data-testid="pph21-page">
      <PageHeader title="PPh 21 Karyawan" />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-32">
          <Label>Tahun</Label>
          <Select data-testid="pph21-year-select" value={year} onChange={(e) => { setYear(Number(e.target.value)); setExpanded(null); }}>
            {[nowY + 1, nowY, nowY - 1, nowY - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>
        <Button data-testid="pph21-export-btn" variant="secondary" disabled={exporting} onClick={exportExcel}>
          <FileSpreadsheet size={14}/> {exporting ? "Menyiapkan..." : "Export Excel"}
        </Button>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase font-bold text-slate-500">Total PPh21 {year}</div>
          <div data-testid="pph21-year-total" className="font-heading font-black text-xl text-slate-900">{fmtIDR(yearTotal)}</div>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">Metode TER (PMK 168/2023), ditanggung perusahaan. Default semua karyawan masuk; klik bulan lalu <b>Take out</b> untuk mengeluarkan karyawan dari bulan itu. Bulan tanpa payroll memakai estimasi gaji pokok — klik ikon pensil di kolom Bruto untuk mengisi bruto manual.</p>

      <Card className="p-0 overflow-hidden">
        <Table>
          <thead><tr>
            <Th className="w-8"></Th><Th>Bulan</Th><Th className="text-right">Karyawan</Th><Th>Sumber</Th><Th className="text-right">Total PPh21</Th><Th>Status</Th><Th>Tgl Bayar</Th><Th className="text-right">Aksi</Th>
          </tr></thead>
          <tbody>
            {months.map((m) => {
              const isOpen = expanded === m.period_month;
              return (
                <React.Fragment key={m.period_month}>
                  <tr data-testid={`pph21-row-${m.period_month}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(isOpen ? null : m.period_month)}>
                    <Td>{isOpen ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}</Td>
                    <Td className="font-bold whitespace-nowrap">{monthName(m.period_month)}</Td>
                    <Td className="text-right">{m.employee_count}{m.excluded_count > 0 && <span className="text-xs text-red-500 ml-1">(−{m.excluded_count})</span>}</Td>
                    <Td className="text-xs text-slate-500">{m.payroll_id ? "Payroll" : "Estimasi"}</Td>
                    <Td className="text-right font-mono font-bold whitespace-nowrap">{fmtIDR(m.total_pph21)}</Td>
                    <Td><span className={`whitespace-nowrap px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${STATUS_BADGE[m.status]}`}>{STATUS_LABEL[m.status]}</span></Td>
                    <Td>{m.paid_at ? fmtDate(m.paid_at) : "-"}</Td>
                    <td className="border-b border-slate-100 px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      {m.status === "unpaid" ? (
                        <Button data-testid={`pph21-pay-btn-${m.period_month}`} variant="success" onClick={() => setPayFor(m)}><Banknote size={13}/> Bayar</Button>
                      ) : m.status === "paid" ? (
                        <span className="text-emerald-600 inline-flex items-center gap-1 text-xs font-bold"><CheckCircle2 size={13}/> Lunas</span>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan={8} className="bg-slate-50 p-3 border-b border-slate-200">
                      <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Rincian per Karyawan — {monthName(m.period_month)}{m.status === "paid" && " (terkunci, sudah dibayar)"}</div>
                      <EmployeeTable month={m} onToggle={toggleEmp} onSaveBruto={saveBruto} busy={busy}/>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </Card>

      {payFor && <PayModal month={payFor} cashAccounts={cashAccounts} onClose={() => setPayFor(null)} onPaid={() => { setPayFor(null); load(); }}/>}
    </div>
  );
}
